/*
 * 背景：规则 4.2 要求图标一律用本地 SVG、不依赖 CDN，工具类产品走 Tabler Icons；
 *   并明确禁止用 emoji 当按钮图标。
 * 设计意图：只内联本页真正用到的十几个图标，不引整包依赖。
 *   全部保持 Tabler 的原始规格（24×24 视口、stroke 2、round 端点），
 *   于是它们和工具页上已有的图标粗细一致，不会显得是另一套。
 * 关键约束：颜色一律 currentColor，由外层文字色决定，不在图标里写死颜色。
 */
import { asset } from './lib/asset'

type IconProps = { size?: number; className?: string }

const base = (size: number) => ({
  width: size,
  height: size,
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 2,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
})

export const IconUpload = ({ size = 24, className }: IconProps) => (
  <svg {...base(size)} className={className} aria-hidden="true">
    <path d="M4 17v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-2" />
    <path d="M7 9l5-5 5 5" />
    <path d="M12 4v12" />
  </svg>
)

export const IconDownload = ({ size = 24, className }: IconProps) => (
  <svg {...base(size)} className={className} aria-hidden="true">
    <path d="M4 17v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-2" />
    <path d="M7 11l5 5 5-5" />
    <path d="M12 4v12" />
  </svg>
)

/** 格子纹指标：点阵，对应频域上规则排列的尖峰 */
export const IconGrid = ({ size = 24, className }: IconProps) => (
  <svg {...base(size)} className={className} aria-hidden="true">
    <path d="M5 5h.01M12 5h.01M19 5h.01M5 12h.01M12 12h.01M19 12h.01M5 19h.01M12 19h.01M19 19h.01" />
  </svg>
)

/** 鳞片指标：横向纹理线 */
export const IconTexture = ({ size = 24, className }: IconProps) => (
  <svg {...base(size)} className={className} aria-hidden="true">
    <path d="M3 3v18h18" />
    <path d="M3 9h18M3 15h18M9 3v18M15 3v18" opacity=".55" />
  </svg>
)

/** 叠瓦指标：分块统计 */
export const IconTiles = ({ size = 24, className }: IconProps) => (
  <svg {...base(size)} className={className} aria-hidden="true">
    <rect x="4" y="4" width="6" height="6" rx="1" />
    <rect x="14" y="4" width="6" height="6" rx="1" />
    <rect x="4" y="14" width="6" height="6" rx="1" />
    <rect x="14" y="14" width="6" height="6" rx="1" />
  </svg>
)

export const IconCheck = ({ size = 24, className }: IconProps) => (
  <svg {...base(size)} className={className} aria-hidden="true">
    <path d="M5 12l5 5L20 7" />
  </svg>
)

export const IconAlert = ({ size = 24, className }: IconProps) => (
  <svg {...base(size)} className={className} aria-hidden="true">
    <path d="M12 9v4M12 17v.01" />
    <path d="M10.24 3.957l-8.422 14.06a1.989 1.989 0 0 0 1.7 2.983h16.845a1.989 1.989 0 0 0 1.7-2.983l-8.423-14.06a1.989 1.989 0 0 0-3.4 0z" />
  </svg>
)

export const IconX = ({ size = 24, className }: IconProps) => (
  <svg {...base(size)} className={className} aria-hidden="true">
    <path d="M18 6L6 18M6 6l12 12" />
  </svg>
)

export const IconLock = ({ size = 24, className }: IconProps) => (
  <svg {...base(size)} className={className} aria-hidden="true">
    <rect x="5" y="11" width="14" height="10" rx="2" />
    <circle cx="12" cy="16" r="1" />
    <path d="M8 11V7a4 4 0 1 1 8 0v4" />
  </svg>
)

/** 重新生成：魔杖，与「会改变画面内容」的语义相配 */
export const IconWand = ({ size = 24, className }: IconProps) => (
  <svg {...base(size)} className={className} aria-hidden="true">
    <path d="M6 21l15-15-3-3L3 18z" />
    <path d="M15 6l3 3" />
    <path d="M9 3l.6 1.8L11.4 5.4 9.6 6 9 7.8 8.4 6 6.6 5.4 8.4 4.8z" />
  </svg>
)

export const IconChevronRight = ({ size = 24, className }: IconProps) => (
  <svg {...base(size)} className={className} aria-hidden="true">
    <path d="M9 6l6 6-6 6" />
  </svg>
)

/** 进行中：环形缺口，由 CSS 旋转 */
export const IconSpinner = ({ size = 24, className }: IconProps) => (
  <svg {...base(size)} className={className} aria-hidden="true">
    <path d="M12 3a9 9 0 1 0 9 9" />
  </svg>
)

export const IconInfo = ({ size = 24, className }: IconProps) => (
  <svg {...base(size)} className={className} aria-hidden="true">
    <circle cx="12" cy="12" r="9" />
    <path d="M12 8h.01M11 12h1v4h1" />
  </svg>
)

export const IconPhoto = ({ size = 24, className }: IconProps) => (
  <svg {...base(size)} className={className} aria-hidden="true">
    <path d="M15 8h.01" />
    <rect x="3" y="3" width="18" height="18" rx="3" />
    <path d="M3 15l4-4a3 3 0 0 1 4 0l5 5" />
    <path d="M14 14l1-1a3 3 0 0 1 4 0l2 2" />
  </svg>
)

/** 换一张图：收起态的重置入口 */
export const IconRefresh = ({ size = 24, className }: IconProps) => (
  <svg {...base(size)} className={className} aria-hidden="true">
    <path d="M20 11A8.1 8.1 0 0 0 4.5 9M4 5v4h4" />
    <path d="M4 13a8.1 8.1 0 0 0 15.5 2M20 19v-4h-4" />
  </svg>
)

/** 已耗时：处理中那一步的计时 */
export const IconClock = ({ size = 24, className }: IconProps) => (
  <svg {...base(size)} className={className} aria-hidden="true">
    <circle cx="12" cy="12" r="9" />
    <path d="M12 7v5l3 3" />
  </svg>
)

/** 数据文件类产物 */
export const IconFile = ({ size = 24, className }: IconProps) => (
  <svg {...base(size)} className={className} aria-hidden="true">
    <path d="M14 3v4a1 1 0 0 0 1 1h4" />
    <path d="M17 21H7a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h7l5 5v11a2 2 0 0 1-2 2z" />
  </svg>
)

/** 报告：与产物文件同一支笔，多三条正文线——它是「一份写好的东西」，不是又一个文件。 */
export const IconReport = ({ size = 24, className }: IconProps) => (
  <svg {...base(size)} className={className} aria-hidden="true">
    <path d="M14 3v4a1 1 0 0 0 1 1h4" />
    <path d="M17 21H7a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h7l5 5v11a2 2 0 0 1-2 2z" />
    <path d="M9 12h6M9 16h6M9 8h1" />
  </svg>
)

/*
 * 米粒（金币）。
 *
 * 2026-09-09 用户裁决：米粒要用单独的 icon。
 *
 * 这一个不是 Tabler 描线图标，而是全站同一份位图素材：
 *   ~/Documents/GitHub/miyang/miyang.cn/static/img/milli-coin.png
 * 已按字节原样复制到 public/img/milli-coin.png（sha256 8972334896128035…），
 * 与 miyang.cn 控制台、Alice 客户端 icons/milli-coin.png 是同一张图。
 *
 * 为什么不改画成描线 SVG：米粒是记账单位，它在控制台、客户端和这里必须是同一枚硬币，
 *   换个画法就成了另一种货币。这一枚的辨识度来自它本身，不来自与图标集的一致。
 * 为什么不直接引 miyang.cn 的地址：那样每次渲染都多一次跨域请求，
 *   而这张图连登录闸都不在，出现在要花钱的那一步——它加载失败的代价是价格旁边空一块。
 * 尺寸沿用控制台那套：句中 13 到 15，统计数字旁 20 到 22，余额大数字旁 38。
 * ?v= 跟着源仓库的版本号走，换素材时两边一起改，否则缓存里会留着旧的那一枚。
 */
export const MilliCoin = ({ size = 20, inline = false }: { size?: number; inline?: boolean }) => (
  <img
    className={`milli-coin${inline ? ' milli-coin-inline' : ''}`}
    src={asset('/img/milli-coin.png?v=20260809')}
    alt="米粒"
    width={size}
    height={size}
    draggable={false}
  />
)
